/**
 * Auth store（Zustand）。
 * - Access token 只在 memory（reload 後靠 refresh cookie 還原）。
 * - Refresh token 放在 httpOnly cookie，前端拿不到；透過 POST /auth/refresh 換新 access。
 */
import axios, { AxiosError } from 'axios';
import { create } from 'zustand';
import type { AuthUser, LoginInput, LoginResponse } from '@app/shared';

/** 後端回應是 {data: ..., meta: ...} envelope（由 TransformInterceptor 產生）。 */
type Envelope<T> = { data: T };

const RAW_BASE = '/api/v1';

/** 獨立的 axios 實例 —— 避免和業務 apiClient 的攔截器互相觸發造成無限 refresh 迴圈。 */
const authRaw = axios.create({
  baseURL: RAW_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  bootstrapped: boolean;

  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<string | null>;
  bootstrap: () => Promise<void>;
  setSession: (user: AuthUser, token: string) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  user: null,
  bootstrapped: false,

  setSession(user, token) {
    set({ user, accessToken: token });
  },

  clear() {
    set({ user: null, accessToken: null });
  },

  async login(input) {
    const res = await authRaw.post<Envelope<LoginResponse>>('/auth/login', input);
    const { user, accessToken } = res.data.data;
    set({ user, accessToken });
  },

  async logout() {
    try {
      await authRaw.post(
        '/auth/logout',
        {},
        { headers: { Authorization: `Bearer ${get().accessToken ?? ''}` } },
      );
    } catch {
      // 忽略：不管後端失不失敗，前端就是清掉。
    }
    set({ user: null, accessToken: null });
  },

  async refresh() {
    try {
      const res = await authRaw.post<Envelope<LoginResponse>>('/auth/refresh');
      const { user, accessToken } = res.data.data;
      set({ user, accessToken });
      return accessToken;
    } catch (err) {
      // refresh 失敗視為登出；不觸發再次 refresh 以免迴圈。
      const status = (err as AxiosError).response?.status;
      if (status === 401 || status === 403) {
        set({ user: null, accessToken: null });
      }
      return null;
    }
  },

  async bootstrap() {
    if (get().bootstrapped) return;
    await get().refresh();
    set({ bootstrapped: true });
  },
}));
